<script setup lang="ts">
import type { IconConstant } from '@tpg/grammar-web-icons';
import { tpgIconPerson, tpgIconSensor } from '@tpg/grammar-web-icons';
import { computed } from 'vue';

import { GrammarDivider } from '@nora-monorepo/grammar-ui';
import type { SymbolDTO } from '@nora-monorepo/nora-api-client';
import { getUserDisplayName } from '@nora-monorepo/shared';

import TrackMenuItem from '#nora-ui/map/components/overlay-menu/items/TrackMenuItem.vue';
import MenuGroup from '#nora-ui/map/components/overlay-menu/primitives/MenuGroup.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface TracksGroupProps {
  mapId: string;
  operationId: string;
}

const props = defineProps<TracksGroupProps>();

/**
 * To avoid clutter, we collapse the Tracks submenu if the number of tracks exceeds this number
 */
const MAX_NUM_OF_TRACKS_BEFORE_COLLAPSING_TRACKS_SUBMENU = 5;

const mapStore = useMapStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

interface TrackRow {
  trackId: string;
  title: string;
  icon: IconConstant;
  goToLabel: string;
  customSymbol?: SymbolDTO | null;
}

const operationUsersWithLastKnownTrack = computed(() => {
  return map.ref.operationUsersWithLastKnownTrack(props.operationId);
});

const operationTrackersWithLastKnownTrack = computed(() => {
  return map.ref.operationTrackersWithLastKnownTrack(props.operationId);
});

/**
 * Users and trackers normalised into a single track-row list, users first, so the
 * submenu can render them through one component and one divider rule.
 */
const tracks = computed<TrackRow[]>(() => {
  const userRows: TrackRow[] = operationUsersWithLastKnownTrack.value.map(user => ({
    trackId: user.id,
    title: getUserDisplayName(user, 'Unknown User'),
    icon: tpgIconPerson,
    goToLabel: 'Go to user',
    customSymbol: user.customSymbol,
  }));

  const trackerRows: TrackRow[] = operationTrackersWithLastKnownTrack.value.map(tracker => ({
    trackId: tracker.id,
    title: tracker.name,
    icon: tpgIconSensor,
    goToLabel: 'Go to tracker',
  }));

  return [...userRows, ...trackerRows];
});

const tracksVisible = computed<boolean>(() => {
  return map.state.track.areAllOperationTracksVisible(props.operationId);
});

/**
 * Represents the visibility of the "Tracks" submenu, which should be visible if there are any users or trackers with a last known track.
 */
const shouldTracksSubmenuBeVisible = computed<boolean>(() => {
  return tracks.value.length > 0;
});

/**
 * Simply expands the tracks submenu initially, if the total number of tracks is low enough to not completely stuff the screen.
 */
const shouldTracksSubmenuBeInitiallyExpanded = computed<boolean>(() => {
  return tracks.value.length <= MAX_NUM_OF_TRACKS_BEFORE_COLLAPSING_TRACKS_SUBMENU;
});

function toggleTrackVisibility() {
  const newValue = !tracksVisible.value;

  map.state.track.setAllOperationTracksVisibility(props.operationId, newValue);
}
</script>

<template>
  <MenuGroup
    v-if="shouldTracksSubmenuBeVisible"
    title="Tracks"
    :visible="tracksVisible"
    :initially-expanded="shouldTracksSubmenuBeInitiallyExpanded"
    :toggle-visibility="toggleTrackVisibility"
    :badge-amount="tracks.length"
  >
    <div
      v-for="(track, index) of tracks"
      :key="track.trackId"
    >
      <TrackMenuItem
        :track-id="track.trackId"
        :title="track.title"
        :icon="track.icon"
        :go-to-label="track.goToLabel"
        :custom-symbol="track.customSymbol"
        :map-id="mapId"
      />

      <GrammarDivider
        v-if="index !== tracks.length - 1"
        emphasis="low-emphasis"
        class="px-2"
      />
    </div>
  </MenuGroup>
</template>
